/**
 * Animator Control — Phase 2B : paramètres du Blind Test réglables à distance
 * depuis le cockpit animateur, avec les mêmes réglages, le même ordre et les mêmes
 * mots que l'écran du jeu (`unblind-test` GameSettings, clés `chooser.settings.*`) :
 * playlist, « Les joueurs devinent », « Comment ils répondent », « Nombre de titres »,
 * « Temps de réponse ».
 *
 * ⚠️ COUPLAGE DOCUMENTÉ (comme la liste GAMES, cf gotcha #5) : cette liste recopie
 * les playlists Deezer INTÉGRÉES du jeu Blind Test. Source de vérité côté jeu :
 *   unblind-test/src/data/playlistsDeezer.data.tsx  (branche airports)
 * Si on ajoute / retire / renomme une playlist intégrée là-bas, METTRE À JOUR ici.
 * Dernière synchro : 2026-10-14 (unblind-test#89, 30 playlists : + Halloween) ;
 * `works` le 18/10/2026 (Disney (English) a désormais ses œuvres côté jeu).
 *
 * Les libellés ne sont PAS recopiés : `nameKey` reprend la clé i18n du jeu, et le
 * cockpit la résout dans SES propres locales (`animator.params.playlists.*`). Une
 * playlist renommée ne se corrige donc qu'à un seul endroit par langue.
 *
 * Les playlists « custom / IA », elles, ne sont PAS recopiées : elles sont
 * persistées sur le compte client (clients/{id}/games/unblind-test/aiPlaylists) et
 * lues dynamiquement par le cockpit. Leur création reste sur l'écran du jeu, mais
 * elles sont désormais lançables à distance.
 *
 * Le cockpit écrit ces valeurs dans Firestore (screenInfos.anim.gameParams) ; l'écran
 * les relaie au jeu (message 'set-game-params') qui les applique à son store
 * useAnimConfig avant le lancement.
 */

/** Famille affichée dans le sélecteur (miroir de `PlaylistRegion` côté jeu). */
export type PlaylistRegion = 'fr' | 'intl';

export type BlindTestPlaylist = {
    id: string;
    /** Clé i18n du libellé, sans le préfixe `animator.params.playlists.`. */
    nameKey: string;
    region: PlaylistRegion;
    /** Nombre de pistes de la playlist Deezer — borne le nombre de chansons jouables. */
    limit: number;
    /**
     * Le jeu connaît l'œuvre (film…) de ses titres (`data` côté jeu) : « L'œuvre » y est
     * jouable. Sans ce drapeau, la TV joue « L'artiste » à la place et la télécommande
     * grise « L'œuvre » avec la raison (retour client 18/10/2026). Contrôlé par `pnpm check:cockpit`.
     */
    works?: true;
};

/** Playlist IA du compte client, lue dans Firestore (pas de miroir en dur). */
export type AiPlaylistOption = {
    id: string;
    name: string;
    /** Nombre de pistes réellement enregistrées — borne le nombre de chansons jouables. */
    trackCount: number;
};

/**
 * Playlists Deezer intégrées (miroir de playlistsDeezer.data.tsx).
 *
 * `limit` = nombre de titres que le jeu reçoit VRAIMENT (l'API Deezer plafonne à
 * 400 par appel), pas le `nb_tracks` affiché par Deezer : c'est lui qui borne le
 * nombre de manches proposé (`limit / 2`).
 */
export const BLIND_TEST_PLAYLISTS: BlindTestPlaylist[] = [
    // ── France ──
    // « Les incontournables » (18/10/2026) : playlist Unboared embarquée dans le jeu,
    // proposée par défaut (EN TÊTE : DEFAULT_BLIND_TEST_PARAMS prend la 1re).
    { id: 'unboared-incontournables-fr', nameKey: 'timelessHits', region: 'fr', limit: 250 },
    { id: '11528232084', nameKey: 'allGenresEasy', region: 'fr', limit: 210 },
    { id: '12958272963', nameKey: 'allGenresHard', region: 'fr', limit: 400 },
    { id: '1884320402', nameKey: 'frenchSongs', region: 'fr', limit: 126 },
    { id: '613860315', nameKey: 'disneyEasy', region: 'fr', limit: 72, works: true },
    { id: '7896165802', nameKey: 'disneyHard', region: 'fr', limit: 232, works: true },
    { id: '6045405764', nameKey: 'decades6070', region: 'fr', limit: 256 },
    { id: '7273946544', nameKey: 'decades80', region: 'fr', limit: 307 },
    { id: '7273887124', nameKey: 'decades90', region: 'fr', limit: 400 },
    { id: '7273969244', nameKey: 'decades2000', region: 'fr', limit: 400 },
    { id: '7273914324', nameKey: 'decades2010', region: 'fr', limit: 400 },
    { id: '6568026624', nameKey: 'frenchRap', region: 'fr', limit: 92 },
    // ── International ──
    { id: 'unboared-incontournables-en', nameKey: 'timelessHitsIntl', region: 'intl', limit: 250 },
    { id: '125020601', nameKey: 'halloween', region: 'intl', limit: 99 },
    { id: '1313621735', nameKey: 'topUsa', region: 'intl', limit: 100 },
    { id: '1470022445', nameKey: 'hits70s', region: 'intl', limit: 70 },
    { id: '867825522', nameKey: 'hits80s', region: 'intl', limit: 100 },
    { id: '878989033', nameKey: 'hits90s', region: 'intl', limit: 100 },
    { id: '1318937087', nameKey: 'hits2000s', region: 'intl', limit: 213 },
    { id: '14917741483', nameKey: 'hits2010s', region: 'intl', limit: 100 },
    { id: '1130102843', nameKey: 'country', region: 'intl', limit: 80 },
    { id: '8291969962', nameKey: 'hipHopClassics', region: 'intl', limit: 124 },
    { id: '10177216462', nameKey: 'classicRock', region: 'intl', limit: 104 },
    { id: '12321647411', nameKey: 'superBowl', region: 'intl', limit: 50 },
    { id: '1962379246', nameKey: 'disneyEnglish', region: 'intl', limit: 53, works: true },
    { id: '13798839621', nameKey: 'films', region: 'intl', limit: 106, works: true },
    { id: '14913966043', nameKey: 'feelGoodPop', region: 'intl', limit: 198 },
    { id: '1111142221', nameKey: 'topUk', region: 'intl', limit: 100 },
    { id: '3885229042', nameKey: 'britpop', region: 'intl', limit: 50 },
    { id: '8893297562', nameKey: 'britishClassics', region: 'intl', limit: 60 },
    { id: '8515679522', nameKey: 'newWave80s', region: 'intl', limit: 50 },
    { id: '8929584182', nameKey: 'indie2000s', region: 'intl', limit: 50 },
];

/**
 * « L'œuvre » jouable avec cette playlist ? Faux seulement pour une playlist intégrée
 * connue SANS œuvres ; une playlist IA, perso ou laissée à la TV (`undefined`) reste
 * permise : la TV, qui connaît ses titres, se replie elle-même sur l'artiste si besoin.
 */
export function playlistAllowsWork(playlistId: unknown): boolean {
    const builtIn = BLIND_TEST_PLAYLISTS.find((p) => p.id === playlistId);
    return !builtIn || builtIn.works === true;
}

/** Paliers de « nombre de chansons » proposés (miroir CUSTOM_STEPS du jeu). */
export const NUM_TRACKS_STEPS = [1, 5, 10, 15, 20, 25, 30, 35, 40];

/** Valeur par défaut (miroir NUM_TRACKS du jeu). */
export const DEFAULT_NUM_TRACKS = 15;

/**
 * « Les joueurs devinent » — miroir de `PROPOSAL_MODES` (unblind-test
 * `src/stores/animConfig/animConfig.tsx`), contrôlé par `pnpm check:cockpit`.
 */
export type ProposalMode = 'artist' | 'title' | 'work';

export const PROPOSAL_MODES: { value: ProposalMode; labelKey: string }[] = [
    { value: 'artist', labelKey: 'animator.params.proposalModes.artist' },
    { value: 'title', labelKey: 'animator.params.proposalModes.title' },
    { value: 'work', labelKey: 'animator.params.proposalModes.work' },
];

/**
 * « Comment ils répondent » — miroir de `ANSWER_MODES` (unblind-test
 * `src/stores/animConfig/animConfig.tsx`), contrôlé par `pnpm check:cockpit`.
 *
 * « À la voix » (`voice`, 16/10/2026) : le joueur maintient un bouton et dit sa
 * réponse (dictée du téléphone, validée par la TV). Place prévue pour « Buzzer »
 * (`buzzer`) : une ligne ici + `animator.params.answerModes.<valeur>` dans les 7
 * langues. Ordre de mise en ligne IMPÉRATIF : le jeu d'abord (il ignore un mode
 * qu'il ne connaît pas, la TV garderait alors son mode sans prévenir), puis ce miroir.
 */
export type AnswerMode = 'multiple' | 'free' | 'voice';

/** Lancement « À la voix » : pastille « Nouveau » jusqu'au 16/11/2026 (lendemain du verdict, même date que le jeu). */
const VOICE_NEW_UNTIL = Date.UTC(2026, 10, 16);

export const ANSWER_MODES: { value: AnswerMode; labelKey: string; newUntil?: number }[] = [
    { value: 'multiple', labelKey: 'animator.params.answerModes.multiple' },
    { value: 'free', labelKey: 'animator.params.answerModes.free' },
    { value: 'voice', labelKey: 'animator.params.answerModes.voice', newUntil: VOICE_NEW_UNTIL },
];

/**
 * « Temps de réponse » — miroir de `REFLECTION_TIME_OPTIONS` (unblind-test
 * `src/config/global.config.ts`), contrôlé par `pnpm check:cockpit`. Valeurs en chaîne
 * (liste de choix) : le jeu les relit avec `parseReflectionTime` et les publie sous la
 * même forme (`reflectionTimeParam`). « Auto » = durée selon le mode (10 s avec 4
 * propositions, 20 s tapé ou dit). Ordre de mise en ligne : le jeu d'abord (il ignore
 * un temps qu'il ne connaît pas), puis ce miroir.
 */
export const REFLECTION_TIMES: { value: string; labelKey: string }[] = [
    { value: 'auto', labelKey: 'animator.params.reflectionTimes.auto' },
    { value: '10', labelKey: 'animator.params.reflectionTimes.s10' },
    { value: '15', labelKey: 'animator.params.reflectionTimes.s15' },
    { value: '20', labelKey: 'animator.params.reflectionTimes.s20' },
    { value: '30', labelKey: 'animator.params.reflectionTimes.s30' },
];

/**
 * Forme des params Blind Test échangés cockpit ↔ jeu. TOUS FACULTATIFS : un champ
 * n'est présent que si l'animateur l'a choisi sur son téléphone, pour que le panneau
 * (qui renvoie tout l'objet à chaque modification) n'écrase jamais le choix fait sur
 * la TV avec une valeur par défaut (gotcha #184). Le jeu ne touche pas à un champ absent.
 */
export type BlindTestParams = {
    playlist?: string;
    numTracks?: number;
    proposalMode?: ProposalMode;
    answerMode?: AnswerMode;
    /** Temps pour répondre : une valeur de `REFLECTION_TIMES`. */
    reflectionTime?: string;
};

/**
 * Replis de re-validation du panneau (nombre de titres devenu impossible), JAMAIS
 * envoyés tels quels. Pas de mode exprès : cf `BlindTestParams`.
 */
export const DEFAULT_BLIND_TEST_PARAMS: BlindTestParams = {
    playlist: BLIND_TEST_PLAYLISTS[0].id,
    numTracks: DEFAULT_NUM_TRACKS,
};

/**
 * Playlist de repli pour un établissement NON francophone : « All-Time Hits »
 * (version internationale des « Incontournables », 18/10/2026 ; avant « 2000s Hits »).
 * Depuis console#211 la playlist n'est plus envoyée tant qu'elle n'est pas choisie au
 * téléphone (le jeu applique alors sa propre règle de langue, unblind-test#87) : ce
 * défaut ne sert plus que de repli interne. Historique ci-dessous.
 *
 * Le défaut historique (`BLIND_TEST_PLAYLISTS[0]`, « Tous genres (facile) ») est
 * une playlist FRANÇAISE. Or le panneau renvoie tous ses réglages dès que
 * l'animateur en touche un seul : un bar anglais qui changeait juste le nombre de
 * chansons partait sur de la chanson française. Choix d'une playlist large et
 * connue des deux côtés de l'Atlantique — la langue ne dit pas si le bar est au
 * Royaume-Uni ou aux États-Unis, donc ni « Top UK » ni « Top USA ».
 *
 * NB : le jeu lui-même démarre aussi sur `playlists[0]` quelle que soit la langue
 * (unblind-test, animConfig) — le cockpit ne recopie pas ce défaut, il le corrige.
 */
export const DEFAULT_INTL_PLAYLIST_ID = 'unboared-incontournables-en';

/**
 * Paliers de chansons valides pour une playlist donnée. Le jeu borne à limit/2
 * (PlaylistTab) ; on reproduit la même règle pour ne pas proposer un nombre que le
 * jeu refuserait. Garantit au moins le 1er palier.
 */
export function numTracksOptionsFor(playlistID: string): number[] {
    const pl = BLIND_TEST_PLAYLISTS.find((p) => p.id === playlistID);
    const max = pl ? Math.floor(pl.limit / 2) : NUM_TRACKS_STEPS[NUM_TRACKS_STEPS.length - 1];
    const opts = NUM_TRACKS_STEPS.filter((n) => n <= max);
    return opts.length > 0 ? opts : [NUM_TRACKS_STEPS[0]];
}

/**
 * Idem pour une playlist IA, dont le nombre de pistes est connu exactement. Le jeu
 * borne à min(nbPistes, 40) et ajoute ce plafond exact comme dernier palier s'il ne
 * tombe pas sur un palier rond (PlaylistTab, branche custom). On reproduit à
 * l'identique pour ne pas proposer un nombre que le jeu refuserait.
 */
export function numTracksOptionsForAi(trackCount: number): number[] {
    const max = Math.min(trackCount, 40);
    const opts = NUM_TRACKS_STEPS.filter((n) => n <= max);
    if (opts.length === 0) return [NUM_TRACKS_STEPS[0]];
    if (opts[opts.length - 1] !== max) opts.push(max);
    return opts;
}

/**
 * Paliers pour la playlist sélectionnée, qu'elle soit intégrée (Deezer) ou IA
 * (compte client). `aiPlaylists` est la liste dynamique lue dans Firestore.
 */
export function numTracksOptionsForPlaylist(playlistID: string, aiPlaylists: AiPlaylistOption[]): number[] {
    const ai = aiPlaylists.find((p) => p.id === playlistID);
    return ai ? numTracksOptionsForAi(ai.trackCount) : numTracksOptionsFor(playlistID);
}
